import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, collection, query, where, onSnapshot, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { StorefrontSectionRenderer } from '../features/vendor/StorefrontEditor';
import ListingCard from '../components/ui/ListingCard';

// ==========================================
// SUB-COMPONENTS / MODALS
// ==========================================

function ImageCropperModal({ imageUrl, aspectRatio, onClose, onCrop }) {
    const imageRef = useRef(null);
    const cropperRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(imageUrl);
    const [currentRatio, setCurrentRatio] = useState(Number.isNaN(aspectRatio) ? null : aspectRatio);

    const initCropper = () => {
        if (cropperRef.current) cropperRef.current.destroy();
        if (imageRef.current && window.Cropper) {
            cropperRef.current = new window.Cropper(imageRef.current, {
                aspectRatio: currentRatio !== null ? currentRatio : NaN,
                viewMode: 1,
                dragMode: 'move',
                background: false,
                autoCropArea: 0.8,
                checkCrossOrigin: false,
                cropend: () => {
                    if (cropperRef.current) {
                        try {
                            const canvas = cropperRef.current.getCroppedCanvas({ width: 400 });
                            if (canvas) setPreviewUrl(canvas.toDataURL('image/jpeg', 0.6));
                        } catch(e) { console.warn("CORS issue", e); }
                    }
                }
            });
        }
    };

    useEffect(() => {
        if (imageRef.current && imageRef.current.complete) initCropper();
        return () => { if (cropperRef.current) cropperRef.current.destroy(); };
    }, []);

    const handleCrop = () => {
        if (cropperRef.current) {
            try {
                const canvas = cropperRef.current.getCroppedCanvas();
                if (!canvas) { alert("Could not generate crop. Please try again."); return; }
                canvas.toBlob((blob) => { onCrop(blob); }, 'image/jpeg');
            } catch(e) { alert("Security error preventing crop."); }
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 overflow-y-auto">
            <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-5xl w-full flex flex-col md:flex-row gap-6 my-auto">
                <div className="w-full md:w-2/3 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Crop Image</h3>
                    <div className="w-full h-64 md:h-96 bg-gray-900 rounded-xl overflow-hidden shadow-inner flex items-center justify-center">
                        <img ref={imageRef} src={imageUrl} alt="Crop preview" style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }} crossOrigin={imageUrl.startsWith('http') ? "anonymous" : undefined} onLoad={initCropper} />
                    </div>
                </div>
                <div className="w-full md:w-1/3 flex flex-col border-t md:border-t-0 md:border-l border-gray-200 pt-6 md:pt-0 md:pl-6">
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Live Preview</h3>
                    <div className="mt-4 flex-grow flex items-center justify-center">
                         <img src={previewUrl} className="max-w-full max-h-48 object-contain rounded-lg border border-gray-200" alt="Preview" />
                    </div>
                    <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-gray-100">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold transition">Cancel</button>
                        <button onClick={handleCrop} className="px-5 py-2.5 rounded-xl bg-pink-600 text-white font-bold transition shadow-md">Crop & Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProfileEditModal({ user, onClose, db, storage }) {
    const [vendorName, setVendorName] = useState(user?.vendorName || '');
    const [description, setDescription] = useState(user?.description || '');
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [coverPhotoFile, setCoverPhotoFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState(user?.profilePicUrl);
    const [coverPhotoPreview, setCoverPhotoPreview] = useState(user?.coverPhotoUrl);
    const [croppingImage, setCroppingImage] = useState(null);
    const [originalImages, setOriginalImages] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [themeColor, setThemeColor] = useState(user?.themeColor || '#DB2777');
    const [fontFamily, setFontFamily] = useState(user?.fontFamily || "'Georgia', serif");
    const [businessInfo, setBusinessInfo] = useState(user?.businessInfo || { address: '', phone: '', website: '' });

    if (!user) return null; 

    const fontOptions = [
        { name: 'Classic Serif', value: "'Georgia', serif" },
        { name: 'Modern Sans', value: "'Helvetica', sans-serif" },
        { name: 'Elegant Script', value: "'Dancing Script', cursive" },
        { name: 'Clean & Simple', value: "'Lato', sans-serif" },
    ];

    const handleBusinessInfoChange = (e) => {
        const { name, value } = e.target;
        setBusinessInfo(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const imgData = { url: reader.result, aspectRatio: type === 'profile' ? 1 : 16 / 9, type };
                setOriginalImages(prev => ({ ...prev, [type]: imgData }));
                setCroppingImage(imgData);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCrop = (blob) => {
        const file = new File([blob], `${croppingImage.type}.jpg`, { type: 'image/jpeg' });
        if (croppingImage.type === 'profile') {
            setProfilePicFile(file);
            setProfilePicPreview(URL.createObjectURL(file));
        } else {
            setCoverPhotoFile(file);
            setCoverPhotoPreview(URL.createObjectURL(file));
        }
        setCroppingImage(null);
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        if (!vendorName.trim()) {
            setError("Store Name is required.");
            setLoading(false);
            return;
        }

        try {
            const userRef = doc(db, "users", user.uid);
            const dataToUpdate = { vendorName, description, themeColor, fontFamily, businessInfo };

            if (profilePicFile) {
                const filePath = `users/${user.uid}/profilePic.jpg`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, profilePicFile);
                dataToUpdate.profilePicUrl = await getDownloadURL(fileRef);
            }
            if (coverPhotoFile) {
                const filePath = `users/${user.uid}/coverPhoto.jpg`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, coverPhotoFile);
                dataToUpdate.coverPhotoUrl = await getDownloadURL(fileRef);
            }

            await updateDoc(userRef, dataToUpdate);
            onClose();
        } catch (err) {
            setError("Failed to save profile. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {croppingImage && (
                <ImageCropperModal imageUrl={croppingImage.url} aspectRatio={croppingImage.aspectRatio} onClose={() => setCroppingImage(null)} onCrop={handleCrop} />
            )}
            <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl md:text-2xl font-bold mb-6">Edit Storefront Profile</h2>
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Store Name</label>
                        <input type="text" value={vendorName} onChange={(e) => setVendorName(e.target.value)} className="mt-1 w-full p-2 border rounded-md" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Store Description</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="4" className="mt-1 w-full p-2 border rounded-md"></textarea>
                    </div>

                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Storefront Design</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Theme Color</label>
                                <div className="mt-2 flex items-center space-x-3">
                                    <input type="color" value={themeColor} onChange={(e) => setThemeColor(e.target.value)} className="w-10 h-10 p-1 border rounded-md cursor-pointer" />
                                    <input type="text" value={themeColor} onChange={(e) => setThemeColor(e.target.value)} placeholder="#DB2777" className="p-2 border rounded-md font-mono text-sm w-32" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Font Style</label>
                                <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} className="mt-1 w-full p-2 border rounded-md">
                                    {fontOptions.map(font => <option key={font.name} value={font.value} style={{ fontFamily: font.value }}>{font.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Information</h3>
                        <div className="space-y-4">
                             <div>
                                <label className="block text-sm font-medium text-gray-700">Business Phone</label>
                                <input type="tel" name="phone" value={businessInfo.phone} onChange={handleBusinessInfoChange} className="mt-1 w-full p-2 border rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Business Address</label>
                                <input type="text" name="address" value={businessInfo.address} onChange={handleBusinessInfoChange} className="mt-1 w-full p-2 border rounded-md" />
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700">Website</label>
                                <input type="url" name="website" value={businessInfo.website} onChange={handleBusinessInfoChange} placeholder="https://example.com" className="mt-1 w-full p-2 border rounded-md" />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Profile Picture (Logo)</label>
                            <img src={profilePicPreview || 'https://placehold.co/150x150/e2e8f0/64748b?text=Logo'} className="w-32 h-32 rounded-full object-cover my-2 border-2" alt="Profile preview" />
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'profile')} className="text-sm" />
                                {originalImages.profile && <button type="button" onClick={() => setCroppingImage(originalImages.profile)} className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md font-semibold hover:bg-blue-100 transition shadow-sm">✂️ Adjust Crop</button>}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Cover Photo</label>
                            <img src={coverPhotoPreview || 'https://placehold.co/400x225/f2a2e8/ffffff?text=Cover'} className="w-full aspect-video object-cover my-2 rounded-md border-2" alt="Cover preview" />
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'cover')} className="text-sm" />
                                {originalImages.cover && <button type="button" onClick={() => setCroppingImage(originalImages.cover)} className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md font-semibold hover:bg-blue-100 transition shadow-sm">✂️ Adjust Crop</button>}
                            </div>
                        </div>
                    </div>
                </div>
                {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                <div className="mt-8 flex justify-end space-x-4">
                    <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-lg">Cancel</button>
                    <button onClick={handleSave} disabled={loading} className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400">
                        {loading ? 'Saving...' : 'Save Profile'}
                    </button>
                </div>
            </div>
        </>
    );
}

function GalleryEditModal({ user, onClose, db, storage }) {
    const [galleryImages, setGalleryImages] = useState(user.galleryImages || []);
    const [newFiles, setNewFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files).map(file => ({ file, preview: URL.createObjectURL(file), caption: '' }));
        setNewFiles(prev => [...prev, ...files]);
    };

    const handleRemoveNew = (index) => setNewFiles(newFiles.filter((_, i) => i !== index));
    const handleRemoveExisting = (index) => setGalleryImages(galleryImages.filter((_, i) => i !== index));

    const handleSave = async () => {
        setLoading(true);
        setError('');
        try {
            const uploadedUrls = [];
            for (const item of newFiles) {
                const filePath = `users/${user.uid}/gallery/${Date.now()}_${item.file.name}`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, item.file);
                const url = await getDownloadURL(fileRef);
                uploadedUrls.push({ url, caption: item.caption });
            }

            const finalGallery = [...galleryImages, ...uploadedUrls];
            await updateDoc(doc(db, "users", user.uid), { galleryImages: finalGallery });
            onClose();

        } catch (err) {
            setError("Failed to save gallery. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <h2 className="text-2xl font-bold mb-4">Edit Gallery</h2>
            <div className="flex-grow overflow-y-auto pr-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {galleryImages.map((img, index) => (
                        <div key={index} className="relative group">
                            <img src={img.url} className="w-full h-32 object-cover rounded-md" alt="" />
                            <button onClick={() => handleRemoveExisting(index)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                        </div>
                    ))}
                    {newFiles.map((item, index) => (
                        <div key={index} className="relative group">
                            <img src={item.preview} className="w-full h-32 object-cover rounded-md" alt="" />
                            <button onClick={() => handleRemoveNew(index)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                        </div>
                    ))}
                    <label htmlFor="gallery-upload" className="cursor-pointer h-32 bg-gray-100 rounded flex items-center justify-center border-2 border-dashed hover:border-blue-400">
                        <span className="text-3xl text-gray-400">+</span>
                    </label>
                    <input id="gallery-upload" type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" />
                </div>
            </div>
            {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
            <div className="mt-6 pt-4 border-t flex justify-end space-x-4">
                <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-lg">Cancel</button>
                <button onClick={handleSave} disabled={loading} className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400">
                    {loading ? 'Saving...' : 'Save Gallery'}
                </button>
            </div>
        </div>
    );
}

function FaqEditModal({ user, onClose, db }) {
    const [faqs, setFaqs] = useState(user.faqs || []);
    const [newQuestion, setNewQuestion] = useState('');
    const [newAnswer, setNewAnswer] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAddOrUpdate = () => {
        if (!newQuestion || !newAnswer) { setError("Question and answer cannot be empty."); return; }
        setError('');
        const newFaq = { question: newQuestion, answer: newAnswer };
        if (editingIndex !== null) {
            const updatedFaqs = [...faqs];
            updatedFaqs[editingIndex] = newFaq;
            setFaqs(updatedFaqs);
        } else {
            setFaqs([...faqs, newFaq]);
        }
        setNewQuestion(''); setNewAnswer(''); setEditingIndex(null);
    };

    const handleEdit = (index) => { const faq = faqs[index]; setNewQuestion(faq.question); setNewAnswer(faq.answer); setEditingIndex(index); };
    const handleDelete = (index) => setFaqs(faqs.filter((_, i) => i !== index));

    const handleSave = async () => {
        setLoading(true); setError('');
        try {
            await updateDoc(doc(db, "users", user.uid), { faqs });
            onClose();
        } catch (err) {
            setError("Failed to save FAQs. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <h2 className="text-2xl font-bold mb-4">Edit FAQs</h2>
            <div className="flex-grow overflow-y-auto pr-4 space-y-4">
                {faqs.map((faq, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md">
                        <p className="font-semibold">{faq.question}</p>
                        <p className="text-sm text-gray-600">{faq.answer}</p>
                        <div className="text-right mt-2 space-x-2">
                            <button onClick={() => handleEdit(index)} className="text-xs text-blue-600 hover:underline">Edit</button>
                            <button onClick={() => handleDelete(index)} className="text-xs text-red-600 hover:underline">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-6 pt-4 border-t space-y-3">
                <h3 className="font-semibold">{editingIndex !== null ? 'Edit FAQ' : 'Add New FAQ'}</h3>
                <input type="text" placeholder="Question" value={newQuestion} onChange={e => setNewQuestion(e.target.value)} className="w-full p-2 border rounded-md" />
                <textarea placeholder="Answer" value={newAnswer} onChange={e => setNewAnswer(e.target.value)} rows="3" className="w-full p-2 border rounded-md"></textarea>
                <button onClick={handleAddOrUpdate} className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm">{editingIndex !== null ? 'Update FAQ' : 'Add FAQ'}</button>
                {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>
            <div className="mt-6 pt-4 border-t flex justify-end space-x-4">
                <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-lg">Cancel</button>
                <button onClick={handleSave} disabled={loading} className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400">
                    {loading ? 'Saving...' : 'Save All FAQs'}
                </button>
            </div>
        </div>
    );
}

function FaqAccordion({ faq }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left py-4">
                <span className="font-semibold text-gray-800">{faq.question}</span>
                <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>▼</span>
            </button>
            {isOpen && <div className="pb-4 text-gray-600">{faq.answer}</div>}
        </div>
    );
}

function VendorGallerySection({ isPublicView, onEdit, pageData }) {
    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Photo Gallery</h3>
                {!isPublicView && <button onClick={onEdit} className="text-sm font-semibold text-blue-600 hover:underline">Edit Gallery</button>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(pageData?.galleryImages || []).length > 0 ? pageData.galleryImages.map((img, index) => (
                    <img key={index} src={img.url} onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/200x200/e2e8f0/64748b?text=Error'; }} alt={img.caption || `Gallery image ${index + 1}`} className="w-full h-48 object-cover rounded-lg shadow-md bg-gray-200" />
                )) : <p className="text-gray-500 col-span-full">The vendor hasn't added any photos to their gallery yet.</p>}
            </div>
        </div>
    );
}

function FaqSection({ isPublicView, onEdit, pageData }) {
    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Frequently Asked Questions</h3>
                {!isPublicView && <button onClick={onEdit} className="text-sm font-semibold text-blue-600 hover:underline">Edit FAQs</button>}
            </div>
            <div>
                 {(pageData?.faqs || []).length > 0 ? pageData.faqs.map((faq, index) => (
                    <FaqAccordion key={index} faq={faq} />
                 )) : <p className="text-gray-500">The vendor hasn't added any FAQs yet.</p>}
            </div>
        </div>
    );
}

function ReviewSection({ reviews }) {
    return (
        <div className="bg-white p-6 rounded-lg shadow space-y-4">
            <h3 className="text-xl font-bold mb-4">Customer Reviews</h3>
            {reviews.length > 0 ? reviews.map(review => (
                <div key={review.id} className="border-t pt-4">
                    <div className="flex items-center mb-2">
                        <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                            ))}
                        </div>
                        <p className="ml-2 text-sm font-semibold">{review.renterName || 'Anonymous'}</p>
                    </div>
                    <p className="text-gray-600 italic">"{review.text}"</p>
                    {review.vendorReply && (
                        <div className="mt-3 ml-4 p-3 bg-gray-50 rounded-lg border-l-2 border-gray-300">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Response from Vendor</p>
                            <p className="text-sm text-gray-700">{review.vendorReply}</p>
                        </div>
                    )}
                </div>
            )) : (
                <div className="border-t mt-4 pt-4">
                    <p className="text-gray-500">No reviews for this vendor yet.</p>
                </div>
            )}
        </div>
    )
}

function ListingSkeleton() {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
            <div className="p-4 space-y-3">
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-3/4 h-6 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-1/2 h-4 bg-gray-200 rounded animate-pulse mt-2"></div>
                <div className="flex items-center gap-2 mt-4">
                    <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse"></div>
                    <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
            </div>
        </div>
    );
}

export default function MyPage({ user, navigateTo, isPublicView, vendorId, db, storage, isMapsScriptLoaded }) { 
    const [pageData, setPageData] = useState(null);
    const [vendorListings, setVendorListings] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true); 
    const [editSection, setEditSection] = useState(null); 
    const [activeTab, setActiveTab] = useState('listings');
    const [scrollScale, setScrollScale] = useState(1);

    const pageUser = isPublicView ? pageData : user;
    const targetId = isPublicView ? vendorId : user?.uid;
    const headerRef = useRef(null);

    const customDataExists = pageUser?.storefront?.sections?.length > 0;
    const isCustomEnabled = pageUser?.storefront?.enabled !== false;
    const showCustomStorefront = customDataExists && isCustomEnabled;

    const toggleStorefrontMode = async () => {
        if (!user || isPublicView) return;
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { "storefront.enabled": !isCustomEnabled });
    };

    useEffect(() => {
        if (!targetId || !db) return;
        setLoading(true);

        const unsubProfile = onSnapshot(doc(db, "users", targetId), (doc) => {
            if (doc.exists()) setPageData(doc.data());
            setLoading(false);
        }, (error) => {
            console.error("Permission denied when fetching storefront profile:", error);
            setLoading(false);
        });

        const qListings = query(collection(db, "listings"), where("vendorId", "==", targetId));
        const unsubListings = onSnapshot(qListings, (snapshot) => {
             setVendorListings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qReviews = query(collection(db, "reviews"), where("vendorId", "==", targetId));
        const unsubReviews = onSnapshot(qReviews, (snapshot) => {
            setReviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const handleScroll = () => {
            const scale = Math.max(0.5, 1 - window.scrollY / 300);
            setScrollScale(scale);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            unsubProfile(); unsubListings(); unsubReviews();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [targetId, db]);

    const startChatWithVendor = async () => {
        const vendorEmail = pageData?.email;
        if (!vendorEmail) {
            alert("Cannot start chat. Vendor information is still loading. Please try again in a moment.");
            return;
        }
        if (!user || user.isAnonymous) {
            navigateTo('login', { vendorId: targetId });
            return;
        }
        if (user.uid === targetId) return; 

        const chatId = [user.uid, targetId].sort().join('_');
        const chatDocRef = doc(db, "chats", chatId);

        await setDoc(chatDocRef, {
            participants: [user.uid, targetId],
            participantEmails: [user.email, vendorEmail],
            participantInfo: {
                [user.uid]: { name: user.vendorName || user.plannerName || user.email, email: user.email, profilePicUrl: user.profilePicUrl || null },
                [targetId]: { name: pageData.vendorName || vendorEmail, email: vendorEmail, profilePicUrl: pageData.profilePicUrl || null }
            },
            updatedAt: serverTimestamp(),
        }, { merge: true });

        navigateTo('messages', { chatId });
    };

    const featuredListings = vendorListings.filter(l => l.isFeatured);
    const regularListings = vendorListings.filter(l => !l.isFeatured);
    const tabClass = (tabName) => `py-4 px-1 border-b-2 font-medium text-sm cursor-pointer ${activeTab === tabName ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`;

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen font-sans">
                <div className="animate-pulse">
                    <div className="h-48 md:h-64 bg-gray-200 w-full"></div>
                    <div className="w-full max-w-[1920px] mx-auto py-8 px-4 md:px-8">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                            <div className="w-full">
                                <div className="relative -mt-20 md:-mt-24 mb-4">
                                    <div className="h-24 w-24 md:h-32 md:w-32 rounded-full border-4 border-white bg-gray-300"></div>
                                </div>
                                <div className="space-y-3 max-w-md">
                                    <div className="h-8 bg-gray-300 rounded-md w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded-md w-1/3 mb-4"></div>
                                    <div className="h-4 bg-gray-200 rounded-md w-full"></div>
                                    <div className="h-4 bg-gray-200 rounded-md w-5/6"></div>
                                </div>
                            </div>
                            <div className="w-full sm:w-auto mt-4 sm:mt-16">
                                <div className="h-10 w-32 bg-gray-300 rounded-full"></div>
                            </div>
                        </div>
                        <div className="mt-8 border-b border-gray-200 flex space-x-8 pb-4">
                            <div className="h-4 bg-gray-300 rounded-md w-24"></div>
                            <div className="h-4 bg-gray-200 rounded-md w-20"></div>
                        </div>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map(i => <ListingSkeleton key={i} />)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (showCustomStorefront) {
        return (
            <div className="bg-white min-h-screen font-sans" style={{ fontFamily: pageUser.storefront.fontFamily || 'inherit' }}>
                {!isPublicView && (
                    <div className="bg-gray-900 text-white p-3 flex flex-wrap gap-2 justify-between items-center px-6 sticky top-[72px] z-40 shadow-md">
                        <span className="text-sm font-semibold">✨ You are viewing your custom storefront</span>
                        <div className="flex items-center gap-3">
                            <button onClick={toggleStorefrontMode} className="bg-gray-700 hover:bg-gray-600 px-4 py-1.5 rounded-full text-sm font-bold transition border border-gray-600 whitespace-nowrap">Switch to Classic View</button>
                            <button onClick={() => navigateTo('dashboard')} className="bg-pink-500 hover:bg-pink-600 px-4 py-1.5 rounded-full text-sm font-bold transition whitespace-nowrap">Back to Dashboard</button>
                        </div>
                    </div>
                )}

                <div className="storefront-content">
                    {pageUser.storefront.sections.map(section => {
                        if (!section.enabled) return null;
                        return (
                            <div key={section.id} id={`storefront-section-${section.id}`}>
                                <StorefrontSectionRenderer 
                                    section={section} 
                                    listings={vendorListings} 
                                    reviews={reviews} 
                                    user={user}
                                    navigateTo={navigateTo}
                                    vendor={pageUser}
                                    startChat={startChatWithVendor}
                                    theme={pageUser.storefront.theme}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-100 min-h-screen">
            {editSection && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
                    {editSection === 'profile' && <ProfileEditModal user={pageUser} onClose={() => setEditSection(null)} db={db} storage={storage} />}
                    {editSection === 'gallery' && <GalleryEditModal user={pageUser} onClose={() => setEditSection(null)} db={db} storage={storage} />}
                    {editSection === 'faq' && <FaqEditModal user={pageUser} onClose={() => setEditSection(null)} db={db} />}
                </div>
            )}

            <div ref={headerRef} className="relative">
                <div className="h-48 md:h-64 bg-gray-200">
                    <img src={pageUser?.coverPhotoUrl || 'https://placehold.co/1200x400/f2a2e8/ffffff?text=Cover+Photo'} onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/1200x400/f2a2e8/ffffff?text=Cover+Photo'; }} className="w-full h-full object-cover" alt="Cover" />
                </div>
                <div className="absolute -bottom-12 left-4 md:left-6" style={{ transform: `scale(${scrollScale})`, transformOrigin: 'bottom left' }}>
                    <img src={pageUser?.profilePicUrl || 'https://placehold.co/150x150/e2e8f0/64748b?text=Logo'} onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x150/e2e8f0/64748b?text=Logo'; }} className="h-24 w-24 md:h-32 md:w-32 rounded-full border-4 border-white object-cover bg-white" alt="Profile" />
                </div>
            </div>

            <div className="w-full max-w-[1920px] mx-auto py-8 px-4 md:px-8">
                <div className="pt-16 sm:pt-20 pb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div>
                        <h2 className="text-3xl font-bold" style={{ color: pageUser?.themeColor, fontFamily: pageUser?.fontFamily }}>{pageUser?.vendorName || pageUser?.email}</h2>
                        
                        <div className="flex items-center mt-1 mb-2">
                            <div className="flex text-yellow-400 text-lg">
                                {[...Array(5)].map((_, i) => (
                                    <span key={i}>{i < Math.round(pageUser?.averageRating || 0) ? '★' : '☆'}</span>
                                ))}
                            </div>
                            <span className="text-gray-700 font-bold ml-2">{(pageUser?.averageRating || 0).toFixed(1)}</span>
                            <span className="text-gray-500 text-sm ml-1">({pageUser?.reviewCount || 0} reviews)</span>
                        </div>

                        <p className="text-gray-600 mt-2" style={{ fontFamily: pageUser?.fontFamily }}>{pageUser?.description || 'No description provided.'}</p>
                        {pageUser?.businessInfo && (
                            <div className="mt-4 text-sm text-gray-500 border-t pt-4">
                                {pageUser.businessInfo.phone && <p><strong>Phone:</strong> <a href={`tel:${pageUser.businessInfo.phone}`} className="text-pink-500 hover:underline">{pageUser.businessInfo.phone}</a></p>}
                                {pageUser.businessInfo.address && <p><strong>Address:</strong> {pageUser.businessInfo.address}</p>}
                                {pageUser.businessInfo.website && <p><strong>Website:</strong> <a href={pageUser.businessInfo.website} target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:underline">{pageUser.businessInfo.website}</a></p>}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {isPublicView && user && !user.isAnonymous && user.uid !== targetId ? (
                            <button onClick={startChatWithVendor} className="bg-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-pink-600 transition self-start sm:self-auto">Message Vendor</button>
                        ) : !isPublicView ? (
                            <>
                                <button onClick={() => setEditSection('profile')} className="bg-white text-gray-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-gray-200 border self-start sm:self-auto">Edit Profile Info</button>
                                {customDataExists && <button onClick={toggleStorefrontMode} className="text-pink-600 text-sm font-bold hover:underline self-start sm:self-auto mt-2 sm:mt-0">Switch to Custom View</button>}
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button onClick={() => setActiveTab('listings')} className={tabClass('listings')}>Offerings</button>
                        <button onClick={() => setActiveTab('gallery')} className={tabClass('gallery')}>Gallery</button>
                        <button onClick={() => setActiveTab('faq')} className={tabClass('faq')}>FAQ</button>
                        <button onClick={() => setActiveTab('reviews')} className={tabClass('reviews')}>Reviews</button>
                    </nav>
                </div>

                <div className="mt-8">
                    {activeTab === 'listings' && (
                        <div>
                             {featuredListings.length > 0 && (
                                <div className="mb-12">
                                    <h3 className="text-2xl font-bold mb-4">Featured Items</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {featuredListings.map(listing => (
                                            <ListingCard 
                                                key={listing.id} 
                                                listing={listing} 
                                                navigateTo={navigateTo} 
                                                user={user} 
                                                isFavorite={user && !user.isAnonymous && user.favorites?.includes(listing.id)}
                                            />
                                        ))} 
                                    </div>
                                    <hr className="my-8"/>
                                </div>
                            )}
                            <h3 className="text-2xl font-bold mb-4">All Offerings</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {regularListings.length > 0 ? (
                                    regularListings.map(listing => (
                                        <ListingCard 
                                            key={listing.id} 
                                            listing={listing} 
                                            navigateTo={navigateTo} 
                                            user={user} 
                                            isFavorite={user && !user.isAnonymous && user.favorites?.includes(listing.id)}
                                        />
                                    ))
                                ) : (
                                    <p className="text-gray-500 col-span-full">This vendor has not added any listings yet.</p>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'gallery' && <VendorGallerySection isPublicView={isPublicView} onEdit={() => setEditSection('gallery')} pageData={pageData} />}
                    {activeTab === 'faq' && <FaqSection isPublicView={isPublicView} onEdit={() => setEditSection('faq')} pageData={pageData} />}
                    {activeTab === 'reviews' && <ReviewSection reviews={reviews} />}
                </div>
            </div>
        </div>
    );
}